import React from "react";
import { Button } from "react-bootstrap";
import Can from "../Can";
import PublicLinkUrlBox from "./PublicLinkUrlBox";
import "./PublicLinkSummaryCell.css";

// Shared "Public Link" table-cell — used by BOTH RoleFormAssignmentsPage.jsx
// (Developer/other roles) and AdminFormAssignmentsPage.jsx's assignment
// tables, driven by the row summary fields services/formAssignmentService.js
// #listAssignments() now returns (active/expired/revoked_public_links_count
// + sole_assignment_user_id) — never a second per-row fetch, never the raw
// token/hash.
//
// Single recipient: an exact badge (No Link/Active/Expired/Revoked) with
// inline actions targeting that one assignee directly.
// Multiple recipients: each has their OWN distinct token, so there is no
// single link to show/copy here — just an "X / Y Active Links" summary and
// a "Manage Links" button that opens the SAME ManageAssignedUsersModal
// every other recipient-management entry point already uses.
const BADGE = {
  none: { className: "plsc-badge-neutral", label: "No Link" },
  active: { className: "plsc-badge-active", label: "Active" },
  expired: { className: "plsc-badge-warn", label: "Expired" },
  revoked: { className: "plsc-badge-revoked", label: "Revoked" },
};

export default function PublicLinkSummaryCell({ row, linkBusyId, onGenerateLink, onRegenerateLink, onRevokeLink, onManageLinks, generatedLinks }) {
  const assignedCount = Number(row.assigned_count) || 0;
  const active = Number(row.active_public_links_count) || 0;
  const expired = Number(row.expired_public_links_count) || 0;
  const revoked = Number(row.revoked_public_links_count) || 0;

  if (assignedCount === 0) {
    return <span className="plsc-muted">No recipients</span>;
  }

  if (assignedCount > 1) {
    return (
      <div className="plsc-cell">
        <span className="plsc-summary">{active} / {assignedCount} Active Link{assignedCount === 1 ? "" : "s"}</span>
        <Can moduleKey="form_assignments" actionKey="assign_users">
          <Button size="sm" variant="outline-primary" className="plsc-manage-btn" onClick={() => onManageLinks(row)}>
            Manage Links
          </Button>
        </Can>
      </div>
    );
  }

  // Exactly one recipient — the exact per-assignee state is fully
  // determined by these three counts (each mutually exclusive; all zero
  // means no link has ever been issued).
  const assignmentUserId = row.sole_assignment_user_id;
  const status = active === 1 ? "active" : expired === 1 ? "expired" : revoked === 1 ? "revoked" : "none";
  const badge = BADGE[status];
  const busy = linkBusyId === assignmentUserId;

  return (
    <div className="plsc-cell">
      <span className={`plsc-badge ${badge.className}`}>
        <span className="plsc-dot" /> {badge.label}
      </span>
      <div className="plsc-actions">
        <Can moduleKey="form_assignments" actionKey="assign_users">
          {status === "none" && (
            <Button size="sm" className="plsc-btn plsc-btn-generate" disabled={busy} onClick={() => onGenerateLink(row.id, assignmentUserId)}>
              {busy ? "…" : "Generate & Copy"}
            </Button>
          )}
          {status === "active" && (
            <Button
              size="sm"
              className="plsc-btn plsc-btn-regenerate"
              disabled={busy}
              onClick={() =>
                onRegenerateLink(row.id, assignmentUserId, {
                  confirmMessage: "Regenerating this link will immediately invalidate the existing public link.\n\nContinue?",
                })
              }
            >
              {busy ? "…" : "Regenerate & Copy"}
            </Button>
          )}
          {(status === "expired" || status === "revoked") && (
            <Button size="sm" className="plsc-btn plsc-btn-generate" disabled={busy} onClick={() => onRegenerateLink(row.id, assignmentUserId, {})}>
              {busy ? "…" : "Generate New Link"}
            </Button>
          )}
        </Can>
        {status === "active" && (
          // Revoke hits DELETE .../public-link, which routes/formAssignmentRoutes.js
          // gates on `remove_users` (not `assign_users`, unlike generate/regenerate) --
          // gate this button on the SAME permission the backend actually enforces.
          <Can moduleKey="form_assignments" actionKey="remove_users">
            <Button
              size="sm"
              className="plsc-btn plsc-btn-revoke"
              disabled={busy}
              onClick={() =>
                onRevokeLink(row.id, assignmentUserId, {
                  confirmMessage: "This will disable public-link access for this user. Their assignment and submission will not be deleted.",
                })
              }
            >
              Revoke
            </Button>
          </Can>
        )}
      </div>
      {/* The full absolute URL a Generate/Regenerate just returned this
          session -- see useFormAssignments.js's `generatedLinks`. Nothing to
          show after a page refresh (only the hash is persisted). */}
      {status === "active" && generatedLinks?.[assignmentUserId] && (
        <PublicLinkUrlBox url={generatedLinks[assignmentUserId]} />
      )}
    </div>
  );
}
